import React from "react";
import { Button, ButtonProps } from "@chakra-ui/react";
import { FiArrowRight } from "react-icons/fi";
import { Link as GatsbyLink } from "gatsby";
import { GetUserDocument, GetUserQuery, useSaveCourseMutation } from "@graphql";
import { getCurrentLesson, hasCourseAccess, useUser } from "@utils";
import { useMarketo } from "@utils/marketo";

type CourseButtonProps = {
  subject?: string;
  course: {
    lessons: ReadonlyArray<
      {
        tasks: readonly Pick<Queries.Task, "id">[];
      } & Pick<Queries.Mdx, "slug">
    >;
  } & Pick<
    Queries.Course,
    | "title"
    | "slug"
    | "progressId"
    | "marketoProgramId"
    | "marketoLeadSourceDetail"
    | "memberships"
    | "tiers"
    | "status"
  >;
  language?: string;
} & ButtonProps;

export function CourseButton({
  subject = "course",
  course,
  language,
  ...props
}: CourseButtonProps) {
  const { user } = useUser();
  const [saveCourse, { loading: savingCourse }] = useSaveCourseMutation({
    update(cache, { data }) {
      const savedCourse = data?.user?.course;
      if (savedCourse) {
        const cacheData = cache.readQuery<GetUserQuery>({
          query: GetUserDocument,
        });

        if (cacheData?.me?.__typename === "User") {
          cache.writeQuery<GetUserQuery>({
            query: GetUserDocument,
            data: {
              me: {
                ...cacheData.me,
                courses: [...(cacheData.me.courses ?? []), savedCourse],
              },
            },
          });
        }
      }
    },
  });

  const handleMarketoPush = useMarketo();

  const {
    progressId: courseId,
    marketoProgramId,
    marketoLeadSourceDetail,
    memberships,
    tiers,
    lessons,
    status,
  } = course;

  if (!user) {
    return (
      <Button
        {...props}
        as={GatsbyLink}
        colorScheme="blue"
        borderRadius={"full"}
        to={`/${lessons[0].slug}`}
        rightIcon={<FiArrowRight />}
        onClick={() =>
          window.gtag?.("event", "course_start", {
            language,
            course_id: courseId,
          })
        }
      >
        Start {subject}
      </Button>
    );
  }

  if (status === "Coming soon") {
    return (
      <Button {...props} isDisabled>
        Coming soon
      </Button>
    );
  }

  if (!hasCourseAccess({ user, memberships, tiers })) {
    return (
      <Button {...props} isDisabled>
        No access
      </Button>
    );
  }

  const currentLesson = getCurrentLesson(lessons, user.tasks ?? []);
  const userCourse = user.courses?.find((course) => course.id === courseId);

  let buttonVerb = "Start";

  if (userCourse) {
    if (userCourse.completedAt) {
      buttonVerb = "Review";
    } else if (currentLesson) {
      buttonVerb = "Resume";
    }
  }

  return (
    <Button
      {...props}
      isDisabled={savingCourse}
      colorScheme="blue"
      borderRadius={"full"}
      as={GatsbyLink}
      to={`/${(currentLesson || lessons[0]).slug}`}
      rightIcon={<FiArrowRight />}
      onClick={() => {
        if (userCourse) {
          window.gtag?.(
            "event",
            userCourse.completedAt
              ? "course_restart_after_finished"
              : "course_resume",
            { language, course_id: courseId }
          );
          return;
        }

        saveCourse({
          variables: {
            userId: user.id,
            course: {
              courseId,
              language,
              isBeta: course.status === "beta",
            },
          },
        });
        window.gtag?.("event", "course_enroll", {
          language,
          course_id: courseId,
        });

        // if we have marketo info in the frontmatter, push to the marketo program for this course
        if (marketoProgramId && marketoLeadSourceDetail) {
          handleMarketoPush({
            status: "Start",
            programId: marketoProgramId.toString(),
            leadSourceDetail: marketoLeadSourceDetail,
            user,
          });
        }
      }}
    >
      {buttonVerb} {subject}
    </Button>
  );
}
